import { useState, useCallback } from 'react'
import {
  verifyPassword,
  hashPassword,
  isValidPassword,
  type AccountRole,
} from '../../accounts'

interface UsePasswordChangeOptions {
  accountId: string
  passwordHash: string | undefined
  role: AccountRole
  updatePassword: (accountId: string, newHash: string) => void
}

interface UsePasswordChangeReturn {
  isChangingPassword: boolean
  currentPassword: string
  newPassword: string
  confirmPassword: string
  passwordError: string
  passwordSuccess: string
  setIsChangingPassword: (v: boolean) => void
  setCurrentPassword: (v: string) => void
  setNewPassword: (v: string) => void
  setConfirmPassword: (v: string) => void
  handleChangePassword: (e: React.FormEvent) => Promise<void>
  resetPasswordForm: () => void
}

export function usePasswordChange({
  accountId,
  passwordHash,
  role,
  updatePassword,
}: UsePasswordChangeOptions): UsePasswordChangeReturn {
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')

  const resetPasswordForm = useCallback(() => {
    setIsChangingPassword(false)
    setPasswordError('')
    setPasswordSuccess('')
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
  }, [])

  const handleChangePassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setPasswordError('')
      setPasswordSuccess('')

      if (!currentPassword || !newPassword || !confirmPassword) {
        setPasswordError('Vul alle velden in.')
        return
      }
      if (newPassword !== confirmPassword) {
        setPasswordError('De nieuwe wachtwoorden komen niet overeen.')
        return
      }
      if (!isValidPassword(newPassword, role)) {
        setPasswordError(
          'Het nieuwe wachtwoord moet minimaal 6 tekens lang zijn.',
        )
        return
      }
      if (!passwordHash) {
        setPasswordError('Dit account heeft geen wachtwoord ingesteld.')
        return
      }

      const isCorrect = await verifyPassword(currentPassword, passwordHash)
      if (!isCorrect) {
        setPasswordError('Het huidige wachtwoord is onjuist.')
        return
      }

      const newHash = await hashPassword(newPassword)
      updatePassword(accountId, newHash)
      setPasswordSuccess('Je wachtwoord is succesvol gewijzigd.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => {
        setIsChangingPassword(false)
        setPasswordSuccess('')
      }, 3000)
    },
    [
      accountId,
      currentPassword,
      newPassword,
      confirmPassword,
      passwordHash,
      role,
      updatePassword,
    ],
  )

  return {
    isChangingPassword,
    currentPassword,
    newPassword,
    confirmPassword,
    passwordError,
    passwordSuccess,
    setIsChangingPassword,
    setCurrentPassword,
    setNewPassword,
    setConfirmPassword,
    handleChangePassword,
    resetPasswordForm,
  }
}
